import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react'

import { Drawer, notification } from 'antd'
import { requestWithPromise, generalErrorHandler, isObject } from '../../utils'
import UserSectorTable, { DATA_TYPE_SELECT, DATA_TYPE_CHECK_LOCAL_ADMINISTRATOR, DATA_TYPE_CHECK_CONTACT_PERSON, DATA_TYPE_TAG } from './UserSectorTable'
import { faTrash } from '@fortawesome/pro-solid-svg-icons'
import { OrderBy, Contexts } from '../../utils/constants'
import { getUser, setUserManagedSectors } from '../../reducers/UserReducer'
import { connect } from 'react-redux'
import { getTranslate } from 'react-localize-redux'
import { HttpMethods, onError, onSuccess } from '../../utils/apiHelper'
import { bindActionCreators } from 'redux'
import Loading from '../../HOC/Loading'
import { getInstitutionSectors } from '../../utils/api/institution'
import { getUserSectors } from '../../utils/api/user'
import { InstitutionContext } from '../../Providers/InstitutionProvider'
import { InstitutionGroupContext } from '../../Providers/InstitutionGroupProvider'
import { GlobalContext } from '../../Providers/GlobalProvider'
import { isHospitalAdmin, isInstitutionGroupAdmin, isNurse } from '../../utils/roles'
import { addContactPerson, addContactPersonToMultipleSectors, addManagedUser, addManagedUserToMultipleSectors } from '../../utils/api/sector'
import { normalizeName } from '../../utils/string'

const deduplicateSectors = sectors => {
  if (!Array.isArray(sectors)) {
    return []
  }

  const uniqueSectors = []
  const seen = new Set()

  sectors.forEach(sector => {
    // Keep the sector id as the primary key to avoid removing distinct care units that share the same generic sector/name.
    const normalizedName = normalizeName(sector?.name, null)
    const deduplicationKey = sector?.id ??
      sector?.genericSector?.id ??
      sector?.genericSector ??
      (sector?.institution?.id ? `${sector.institution.id}-${normalizedName}` : normalizedName)

    if (deduplicationKey !== null && deduplicationKey !== undefined && !seen.has(deduplicationKey)) {
      seen.add(deduplicationKey)
      uniqueSectors.push(sector)
    }
  })

  return uniqueSectors
}

const mapStateToProps = state => ({ user: getUser(state.getUser), t: getTranslate(state.locale) })

const mapDispatchToProps = dispatch => ({ setReduxUserManagedSectors: bindActionCreators(setUserManagedSectors, dispatch) })

const UserManagedSectors = ({ user, schoolId, selectedUser, onClose, setReduxUserManagedSectors, t }) => {
  const ic = useContext(InstitutionContext)
  const igContext = useContext(InstitutionGroupContext)
  const globalContext = useContext(GlobalContext)

  const [userAffiliatedSectors, setUserAffiliatedSectors] = useState([])
  const [userAvailableSectors, setUserAvailableSectors] = useState([])
  const [userSectors, setUserSectors] = useState({ contactPersons: [], affiliated: [] })

  const [loading, setLoading] = useState(false)

  const sectors = useMemo(() => (isObject(ic) ? ic.sectors : []), [ic])
  const archivedSectors = useMemo(() => (isObject(ic) ? ic.archivedSectors : []), [ic])
  const selectableSectors = useMemo(() => {
    // Always expose the sectors currently linked to the user, even when the fetched list is empty.
    const baseSectors = userAvailableSectors.length ? userAvailableSectors : [...userAffiliatedSectors, ...sectors]
    const deduplicated = deduplicateSectors(baseSectors)

    return userAvailableSectors.length ? deduplicated.filter(sector => !sector.archived) : deduplicated
  }, [userAvailableSectors, userAffiliatedSectors, sectors])

  const isSuperAdmin = useMemo(() => user.context === Contexts.ADMIN, [user])
  const affiliatedSectors = useMemo(() => {
    return userAffiliatedSectors.map(s => {
      return {
        id: s.id,
        sector: s.id,
        name: s.name,
        archived: s.archived
      }
    }).filter(s => !s.archived || archivedSectors.some(si => si.id === s.id))
  }, [userAffiliatedSectors, archivedSectors])

  const selectedUserInstitutions = useMemo(() => {
    if (!selectedUser) {
      return []
    }

    let institutions = Array.isArray(selectedUser.institutions) ? selectedUser.institutions : []

    if (!institutions.length && selectedUser.id === user.id) {
      institutions = Array.isArray(user.institutions) ? user.institutions : []
    }

    // When the selected user has no institution set yet, fallback to the admin's institution scope.
    if (!institutions.length && igContext?.institutions?.length) {
      institutions = igContext.institutions
    } else if (!institutions.length && globalContext?.institutions?.length) {
      institutions = globalContext.institutions
    }

    return institutions
      .map(institution => {
        if (isObject(institution)) {
          return institution
        }

        return { id: institution }
      })
      .filter(institution => institution?.id)
  }, [selectedUser, user, igContext?.institutions, globalContext?.institutions])

  const fetchAdminSectors = useCallback((parameters = {}) => {
    if (!selectedUserInstitutions.length) {
      return Promise.resolve([])
    }

    const baseParameters = { orderBy: OrderBy.NAME, strictMode: 0, ...parameters }
    const requests = selectedUserInstitutions.map(institution => getInstitutionSectors(user, institution, { ...baseParameters }))

    return Promise.all(requests).then(responses => {
      const aggregatedSectors = []

      responses.forEach(response => {
        if (response?.data) {
          response.data.forEach(sector => {
            if (!aggregatedSectors.some(existing => existing.id === sector.id)) {
              aggregatedSectors.push(sector)
            }
          })
        }
      })

      return deduplicateSectors(aggregatedSectors)
    })
  }, [selectedUserInstitutions, user])

  useEffect(() => {
    if (!selectedUser) {
      setUserAffiliatedSectors([])

      return
    }

    let mounted = true
    setLoading(true)

    const request = (isHospitalAdmin(selectedUser) || isInstitutionGroupAdmin(selectedUser))
      ? fetchAdminSectors()
      : getUserSectors(user, selectedUser).then(json => json?.data ?? [])

    request
      .then(sectors => {
        if (mounted) {
          loadUser(sectors)
        }
      })
      .catch(error => {
        generalErrorHandler(error)

        if (mounted) {
          setUserAffiliatedSectors([])
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [fetchAdminSectors, selectedUser, user])

  useEffect(() => {
    let mounted = true

    if (!selectedUserInstitutions.length) {
      setUserAvailableSectors([])

      return () => {
        mounted = false
      }
    }

    fetchAdminSectors({ strictMode: 1 }).then(sectors => {
      if (mounted) {
        setUserAvailableSectors(deduplicateSectors(sectors))
      }
    }).catch(error => {
      generalErrorHandler(error)

      if (mounted) {
        setUserAvailableSectors([])
      }
    })

    return () => {
      mounted = false
    }
  }, [fetchAdminSectors, selectedUserInstitutions])

  const loadUser = sectors => {
    const uniqueSectors = deduplicateSectors(sectors)
    const userSectors = {
      contactPersons: [],
      affiliated: []
    }

    uniqueSectors.forEach(s => {
      userSectors.contactPersons[s.id] = s.contactPersons.map(contactPerson => {
        return contactPerson.id
      })
      userSectors.affiliated[s.id] = s.affiliates.map(user => {
        return user.id
      })
    })

    setUserSectors(userSectors)
    setUserAffiliatedSectors(uniqueSectors)
  }

  const handleSectorAddition = sector => {
    if (sector.newId === null) {
      return
    }

    const newSector = selectableSectors.find(s => s.id === sector.newId)

    if (!newSector) {
      onError(t('common.error'))
      return
    }

    let affiliatedSectors = userAffiliatedSectors
    const userManagedSectorIds = affiliatedSectors.map(s => s.id)

    if (userManagedSectorIds.includes(newSector.id)) {
      onError(t('user_managed_sectors.duplicated_sector'))
      return
    } else {
      affiliatedSectors = userAffiliatedSectors.concat(newSector)
    }

    handleUserCoordinatedSectorUpdating(affiliatedSectors)
  }

  const handleSectorEdition = sector => {
    const editedSector = selectableSectors.find(s => s.id === sector.newId)

    if (!editedSector) {
      onError(t('common.error'))
      return
    }
    const userManagedSectorIds = affiliatedSectors.map(s => s.id)

    if (userManagedSectorIds.includes(editedSector.id)) {
      onError(t('user_managed_sectors.duplicated_sector'))

      return
    }

    handleUserCoordinatedSectorUpdating(userAffiliatedSectors.map(ums => ums.id === sector.prevId ? editedSector : ums))
  }

  const handleSectorDeletion = sector => {
    const affiliatedSectors = userAffiliatedSectors.filter(ums => ums.id !== sector.sector)

    handleUserCoordinatedSectorUpdating(affiliatedSectors, sector.sector)
  }

  const handleUserCoordinatedSectorUpdating = (userAffiliatedSectors, sectorId = null, confirmationMessage = false) => {
    if (selectedUser.id === user.id) {
      setReduxUserManagedSectors(userAffiliatedSectors)
    }

    setUserAffiliatedSectors(userAffiliatedSectors)

    const body = { affiliatedSectors: userAffiliatedSectors.map(ums => ums.id) }

    if (isSuperAdmin && schoolId) {
      body.schoolId = schoolId
    }

    if (sectorId && isNurse(selectedUser) && userIsContactPerson(sectorId)) {
      body.sectorToRemoveContactPerson = sectorId
    }

    requestWithPromise('/api/users/' + selectedUser.id, HttpMethods.PATCH, body, user)
      .then((json) => {
        if (confirmationMessage) {
          notification.success({ message: t('Saved changes'), placement: 'bottomRight' })
        }
      })
      .catch((error) => { generalErrorHandler(error) })
  }

  const userIsContactPerson = sectorId => {
    if (selectedUser && userSectors.contactPersons[sectorId]) {
      return userSectors.contactPersons[sectorId].includes(selectedUser.id)
    }
  }

  const userIsAdmin = sectorId => {
    if (selectedUser && userSectors.affiliated[sectorId]) {
      return userSectors.affiliated[sectorId].includes(selectedUser.id)
    }
  }

  const setUserContactPerson = sectorId => {
    addContactPerson(user, { id: sectorId }, { userToAdd: selectedUser.id }).then(() => onSuccess(t('Saved changes'), 'bottomLeft'))
  }

  const setManagedUser = sectorId => {
    addManagedUser(user, { id: sectorId }, { userToAdd: selectedUser.id }).then(() => onSuccess(t('Saved changes'), 'bottomLeft'))
  }

  const setMultipleUserContactPersons = sectors => {
    addContactPersonToMultipleSectors(user, { userToAdd: selectedUser.id, sectors: sectors }).then(() => onSuccess(t('Saved changes'), 'bottomLeft'))
  }

  const setMultipleManagedUsers = sectors => {
    addManagedUserToMultipleSectors(user, { userToAdd: selectedUser.id, sectors: sectors }).then(() => onSuccess(t('Saved changes'), 'bottomLeft'))
  }

  const searchWithFilter = (value, isAdmin) => {
    if (!selectedUser) {
      return
    }

    if (isAdmin) {
      fetchAdminSectors(value ? { search: value } : {}).then(sectors => {
        loadUser(sectors)
      }).catch(error => generalErrorHandler(error))
    } else {
      getUserSectors(user, selectedUser, { search: value }).then(json => {
        if (json?.data) {
          loadUser(json.data)
        }
      })
    }
  }

  const columns = useMemo(() => {
    const columns = [{ type: DATA_TYPE_SELECT, name: t('common.sector'), options: selectableSectors }]

    if (isHospitalAdmin(selectedUser)) {
      columns.push({ type: DATA_TYPE_CHECK_LOCAL_ADMINISTRATOR, name: t('Local administrator'), style: { width: '150px' } })
    }

    columns.push({ type: DATA_TYPE_CHECK_CONTACT_PERSON, name: t('common.contact_persons'), style: { width: '150px' } })
    columns.push({ type: DATA_TYPE_TAG, name: t('Status'), style: { width: '50px' } })

    return columns
  }, [t, selectableSectors, selectedUser])

  const additionalActions = [
    {
      icon: faTrash,
      type: 'danger',
      title: 'Delete',
      titlePopconfirm: t('Delete this sector?'),
      handleOnClick: handleSectorDeletion
    }
  ]

  return (
    <Drawer
      title={isObject(selectedUser) ? t('Sectors managed by') + ' ' + selectedUser.email : '?'}
      width='840px'
      onClose={onClose}
      visible={selectedUser !== null}
    >
      <Loading loading={loading}>
        <UserSectorTable
          onDataAdd={handleSectorAddition}
          onDataEdit={handleSectorEdition}
          data={affiliatedSectors}
          columns={columns}
          additionalActions={additionalActions}
          userIsContactPerson={userIsContactPerson}
          userIsAdmin={userIsAdmin}
          setUserContactPerson={setUserContactPerson}
          setManagedUser={setManagedUser}
          setMultipleUserContactPersons={setMultipleUserContactPersons}
          setMultipleManagedUsers={setMultipleManagedUsers}
          user={selectedUser}
          searchWithFilter={searchWithFilter}
        />
      </Loading>
    </Drawer>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(UserManagedSectors)
